import { z } from "zod";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSnackbar } from "notistack";

import { Button } from "@components/ui/button";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { resetPassword } from "@/services/auth.service";
import { getErrorFromBackend } from "@/utils/errors";
import { Loader2 } from "lucide-react";
import { PATH_LOGIN } from "@/routes/paths";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";


type Inputs = {
  password: string;
  confirmPassword: string;
};

const createPasswordSchema = z
  .object({
    password: z.string({
      required_error: "New Password is required",
    }),
    confirmPassword: z.string({
      required_error: "Confirm Password is required",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords must match",
    path: ["confirmPassword"],
  });

type Props = {
  token: string;
};
const ResetPasswordForm = ({ token }: Props) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const methods = useForm<Inputs>({
    resolver: zodResolver(createPasswordSchema),
  });
  const { handleSubmit, formState, control } = methods;
  const { isSubmitting } = formState;

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      await resetPassword(data.password, token);
      navigate(PATH_LOGIN);
      enqueueSnackbar("Password has been reset, you can login now", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(getErrorFromBackend(error), { variant: "error" });
    }

  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-6">
          <FormField
            control={control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-gray-800">
                  New Password <span className="text-red-500">*</span>
                </FormLabel>

                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      className="pr-10 text-slate-900"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </FormControl>

                <FormMessage className="text-xs text-red-500" />
              </FormItem>
            )}
          />
        </div>

        <div className="mb-6">
          <FormField
            control={control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-gray-800">
                  Confirm Password <span className="text-red-500">*</span>
                </FormLabel>

                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      className="pr-10 text-slate-900"
                    />

                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </FormControl>

                <FormMessage className="text-xs text-red-500" />
              </FormItem>
            )}
          />
        </div>

        <Button disabled={isSubmitting} className="w-full" type="submit">
          {isSubmitting ? <Loader2 className="animate-spin" /> : "Reset Password"}
        </Button>
      </form>
    </FormProvider>
  );
};

export default ResetPasswordForm;
